import instance, { baseURL, isOpPortal } from './index';
import { message } from 'antd';
import { AxiosResponse, AxiosRequestConfig } from 'axios';
import { getScheduleToken, getAdminToken, getUserToken, getUserId } from 'utils/localstore';
import { getPreviewScheduleToken, getPreviewAdminToken } from 'utils/sessionStorage';

export const ERROR_CODE = {
    TOKEN_UN_VERIFY: 4001,
};

const parseJSON = async (response: AxiosResponse): Promise<any> => {
    if (response.status >= 200 && response.status < 400) {
        if (response.status === 204) return {};
        const body = response.data;
        if (body && body.code !== undefined && body.code !== 200) {
            const errorMsg = body.message || body.error || 'Request failed';
            throw new Error(errorMsg);
        }
        return { data: body, error: undefined };
    }
    if (response.status >= 400 && response.status < 500) {
        const errorText = response.data?.message ?? response.data?.error;
        throw new Error(errorText ?? `${response.status} ${response.statusText}`);
    }

    if (response.status >= 500 && response.status < 600) {
        // exception in backend
        const errorText = response.data?.message ?? response.data?.error;
        throw new Error(errorText ?? `${response.status} ${response.statusText}`);
    }

    throw new Error(`${response.status} ${response.statusText}: Unexpected status code`);
};

const handleErrorMessage = (err?: any) => {
    try {
        const msg: string = err?.message;
        if (msg && !msg.includes('unauthorized')) {
            message.error(err?.message);
        }
    } catch (e) {}
};

const handleErrorCode = (error: any): void => {
    if (error?.data?.code === ERROR_CODE.TOKEN_UN_VERIFY) {
        // if (isOpPortal()) {
        //     window.navigate(PATH.HOME);
        // } else {
        //     window.navigate(PATH.SIGNUP);
        // }
        // window.location.reload();
    }
};

type RequestParam<T> = {
    url: string;
    params?: T;
    config?: AxiosRequestConfig;
    disableErrorNote?: boolean;
    disableToken?: boolean;
    scheduleToken?: string;
    overrideProviderId?: number | string;
};

const buildQueryString = (params: unknown): string => {
    if (!params || typeof params !== 'object') return '';
    return Object.entries(params as Record<string, unknown>)
        .filter(([, v]) => v !== undefined && v !== null)
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
        .join('&');
};

const getHeader = (disableToken?: boolean, url?: string, overrideScheduleToken?: string) => {
    const ret: any = {
        'Content-Type': 'application/json;charset=UTF-8',
    };
    const scheduleToken = overrideScheduleToken || getPreviewScheduleToken() || getScheduleToken();
    const adminToken = getPreviewAdminToken() || getAdminToken();
    const isOpsUrl = url?.includes('/admin-server/');
    const isScheduleUrl = url?.includes('/schedule-server/');
    if (scheduleToken && !disableToken && isScheduleUrl) {
        ret['schedule-token'] = scheduleToken;
    }
    // if (getUserToken() && !disableToken) {
    //     ret.token = getUserToken();
    // }
    if (adminToken && !disableToken && isOpsUrl) {
        ret['admin-token'] = adminToken;
    }
    if (!isOpsUrl && !isScheduleUrl && !disableToken) {
        if (scheduleToken) {
            ret['schedule-token'] = scheduleToken;
        }
        if (adminToken) {
            ret['admin-token'] = adminToken;
        }
    }
    // if (url?.includes('admin-server/ops/switch/exit')) {
    //     //特殊处理:https://applink.larksuite.com/client/message/link/open?token=AmkhyZ%2BsgA21agLT4h9ZEHI%3D
    //     ret['admin-token'] = adminToken;
    //     ret['schedule-token'] = scheduleToken;
    // }
    // if (getUserToken() && !disableToken) {
    //     ret.token = getUserToken();
    // }
    return ret;
};

const buildConfig = (
    config: AxiosRequestConfig | undefined,
    disableToken: boolean | undefined,
    url: string,
    scheduleToken: string | undefined,
    overrideProviderId?: number | string,
): AxiosRequestConfig => {
    const headers: Record<string, any> = { ...getHeader(disableToken, url, scheduleToken), ...(config?.headers || {}) };
    if (overrideProviderId !== undefined && overrideProviderId !== null) {
        const isSelf = `${getUserId()}` === `${overrideProviderId}`;
        if (!isSelf) {
            headers['OVERRIDE-PROVIDER-ID'] = String(overrideProviderId);
        }
    }
    const base = config ? { ...config, headers } : { headers };
    if (scheduleToken) {
        (base as any)._skipTokenInterceptor = true;
    }
    return base;
};

export class Request {
    static get = async <T>(props: RequestParam<T>) => {
        const { url, config, params, disableErrorNote, disableToken, scheduleToken, overrideProviderId } = props;
        try {
            const paramStr = buildQueryString(params);
            const urlWithParam = paramStr ? `${url}?${paramStr}` : url;
            const response = await instance.get(
                urlWithParam,
                buildConfig(config, disableToken, url, scheduleToken, overrideProviderId),
            );
            return await parseJSON(response);
        } catch (err) {
            const error = err as any;
            handleErrorCode(error);
            const errorMsg =
                error?.message ||
                error?.data?.message ||
                'Oops! Our system is on a coffee break. Please try again later.';
            !disableErrorNote && handleErrorMessage({ message: errorMsg });
            return { success: false, error: errorMsg, errorCode: error?.data?.code };
        }
    };

    static post = async <T>(props: RequestParam<T>) => {
        const { url, params, config, disableErrorNote, disableToken, scheduleToken, overrideProviderId } = props;
        try {
            const response = await instance.post(
                url,
                params,
                buildConfig(config, disableToken, url, scheduleToken, overrideProviderId),
            );
            return await parseJSON(response);
        } catch (err) {
            const error = err as any;
            handleErrorCode(error);
            const errorMsg =
                error?.message ||
                error?.data?.message ||
                error?.data?.error ||
                'Oops! Our system is on a coffee break. Please try again later.';
            !disableErrorNote && handleErrorMessage({ message: errorMsg });
            return { success: false, error: errorMsg, errorCode: error?.data?.code };
        }
    };

    static put = async <T>(props: RequestParam<T>) => {
        const { url, params, config, disableErrorNote, disableToken, scheduleToken, overrideProviderId } = props;
        try {
            const response = await instance.put(
                url,
                params,
                buildConfig(config, disableToken, url, scheduleToken, overrideProviderId),
            );
            return await parseJSON(response);
        } catch (err) {
            const error = err as any;
            handleErrorCode(error);
            const errorMsg =
                error?.message ||
                error?.data?.message ||
                'Oops! Our system is on a coffee break. Please try again later.';
            !disableErrorNote && handleErrorMessage({ message: errorMsg });
            return { success: false, error: errorMsg, errorCode: error?.data?.code };
        }
    };

    static delete = async <T>(props: RequestParam<T>) => {
        const { url, config, params, disableErrorNote, disableToken, scheduleToken, overrideProviderId } = props;
        try {
            let urlWithParam = url;
            const requestConfig = buildConfig(config, disableToken, url, scheduleToken, overrideProviderId);
            if (params instanceof Array) {
                requestConfig.data = params;
            } else {
                const paramStr = buildQueryString(params);
                urlWithParam = paramStr ? `${url}?${paramStr}` : url;
            }

            const response = await instance.delete(urlWithParam, requestConfig);
            return await parseJSON(response);
        } catch (err) {
            const error = err as any;
            handleErrorCode(error);
            const errorMsg =
                error?.message ||
                error?.data?.message ||
                'Oops! Our system is on a coffee break. Please try again later.';
            !disableErrorNote && handleErrorMessage({ message: errorMsg });
            return { success: false, error: errorMsg, errorCode: error?.data?.code };
        }
    };

    static streamPost = async <T>(props: RequestParam<T>) => {
        const { url, params, config, disableErrorNote, disableToken, scheduleToken, overrideProviderId } = props;
        const header: any = { ...getHeader(disableToken, url, scheduleToken), ...(config?.headers || {}) };
        if (overrideProviderId !== undefined && overrideProviderId !== null) {
            const isSelf = `${getUserId()}` === `${overrideProviderId}`;
            if (!isSelf) {
                header['OVERRIDE-PROVIDER-ID'] = String(overrideProviderId);
            }
        }
        const _url = url.startsWith('http') ? url : baseURL + url;
        return fetch(_url, {
            method: 'POST',
            headers: {
                ...header,
                'Content-Type': 'application/json',
                Accept: 'text/event-stream', // 接收服务器端的 streaming 数据
            },
            body: JSON.stringify(params),
        });
    };

    static getBlob = async <T>(props: RequestParam<T>) => {
        const { url, config, params, disableErrorNote, disableToken, scheduleToken, overrideProviderId } = props;
        try {
            const paramStr = buildQueryString(params);
            const urlWithParam = paramStr ? `${url}?${paramStr}` : url;
            const response = await instance.get(
                urlWithParam,
                buildConfig(config, disableToken, url, scheduleToken, overrideProviderId),
            );
            return new Blob([response.data], { type: 'application/pdf' });
        } catch (err) {
            const error = err as any;
            handleErrorCode(error);
            const errorMsg =
                error?.message ||
                error?.data?.message ||
                'Oops! Our system is on a coffee break. Please try again later.';
            !disableErrorNote && handleErrorMessage({ message: errorMsg });
            return { success: false, error: errorMsg, errorCode: error?.data?.code };
        }
    };
}
